import React from 'react';

import { Hero } from '@/administration/pages/Journey/common/molecules/JourneyHero';
import { useCurrentUserAccess } from '@/store/currentUser/userCurrentUserAccess';
import { StartProgressOverview } from '@/features/course/learner/atoms/StartProgressOverview';
import { StartAdminButtonGroup } from '@/features/course/learner/atoms/StartAdminButtonGroup';
import { StartDescription } from '@/features/course/learner/atoms/StartDescription';
import { UseCourseViewerV2Result } from '@/administration/pages/Course/store/courseViewer/useCourseViewerV2';
import { StartLayout } from '@/features/course/learner/atoms/StartLayout/StartLayout';
import { BackToParentButton } from '@/features/course/learner/atoms/BackToParentButton';
import { useLearnerCourseHref } from '@/features/course/learner/hooks/useLearnerCourseNavigate';
import { LearningJourneyItem } from '@/store/v2/journeys/journey';
import { StartUpNext } from '@/features/course/learner/atoms/StartUpNext/StartUpNext';
import { RLearningContentProvider } from '@/store/v2/remote.gen';
import { LearningTypeLabel } from '@/ui/Label';
import { useLearningJourneyBreadcrumbs } from '@/store/v2/journeys/useLearningJourneyBreadcrumbs';

type CourseStartProps = UseCourseViewerV2Result & {
  nextItem?: LearningJourneyItem;
  allUpcomingItemsLocked?: boolean;
  allUpcomingItemsUnlockAt?: Date | string;
};

export const CourseStart = ({
  course,
  progress,
  nextItem,
  allUpcomingItemsLocked,
  allUpcomingItemsUnlockAt,
  ...props
}: CourseStartProps): JSX.Element => {
  const { isAdmin, loading } = useCurrentUserAccess();
  const href = useLearnerCourseHref();
  const displayAdminButtonGroup = !loading && isAdmin;

  const isLoading = props.loadingLearning || !props.loadingProgressInitialized;

  const learningJourneyBreadcrumbs = useLearningJourneyBreadcrumbs({
    id: Number(course?.id),
  });

  if (isLoading || !course) {
    return (
      <StartLayout
        hero={<Hero.LoadingState />}
        main={<StartDescription.LoadingState />}
        sidebar={<StartProgressOverview.LoadingState />}
      />
    );
  }

  return (
    <StartLayout
      headerStart={<BackToParentButton courseId={Number(course.id)} />}
      headerEnd={
        displayAdminButtonGroup ? (
          <StartAdminButtonGroup
            course={{ id: Number(course.id), title: course.title }}
            editUrl={href.adminEditorHref({ id: Number(course.id), title: course.title })}
          />
        ) : (
          <></>
        )
      }
      hero={
        <Hero
          isSaved={props.isCourseSaved}
          onSave={props.onSaveCourse}
          title={course.title}
          imageUrl={course.image}
          provider={RLearningContentProvider.Collegial}
        />
      }
      main={
        <>
          <StartDescription headline="Description" description={course.teaser} />
          <div>
            <LearningTypeLabel type="COURSE" text="Course" />
          </div>
        </>
      }
      sidebar={
        <>
          <StartProgressOverview
            course={course}
            progress={progress}
            onStart={props.startCourse}
            startLoading={props.loadingStartCourse}
            onContinue={props.continueCourse}
            onRevisit={props.revisitCourse}
            startedHeadline="Course progress"
          />
          {!!progress?.isCompleted && (!!nextItem || allUpcomingItemsLocked) && (
            <StartUpNext
              learningCardProps={
                nextItem
                  ? {
                      objectId: nextItem.contentId,
                      title: nextItem.content?.title,
                      imageUrl: nextItem.content?.imageURL,
                      type: nextItem.content?.metadata?.type,
                      level: nextItem.content?.metadata?.level,
                      totalEffort: nextItem.content?.plannedDuration,
                    }
                  : undefined
              }
              actionHref={
                nextItem?.content?.curations?.[0]?.launchURLPath ? `${nextItem.content.curations[0].launchURLPath}` : '#'
              }
              parentJourneyId={learningJourneyBreadcrumbs?.journeyId}
              allUpcomingItemsLocked={allUpcomingItemsLocked}
              unlockAt={allUpcomingItemsUnlockAt}
            />
          )}
        </>
      }
    />
  );
};
